import React, { useState, useEffect, useCallback, useRef } from "react";
import { db } from "../firebase";
import {
  collection,
  query,
  orderBy,
  onSnapshot,
  where,
  getDoc,
  doc,
  updateDoc,
  deleteDoc,
  setDoc,
  Timestamp,
} from "firebase/firestore";
import {
  FaTrash,
  FaEdit,
  FaPlus,
  FaWindowClose,
  FaFilePdf,
  FaFileCsv,
  FaChevronDown,
  FaChevronUp,
} from "react-icons/fa";
import { useAuth } from "../contexts/AuthContext";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { Link } from "react-router-dom";
import { hasPermission, ROLES, FEATURES } from "../lib/rolesConfig";
import MultiActionScrollButton from "../components/MultiActionScrollButton";
import { commitFirestoreWrite, offlineAwareMessage } from "../utils/offline";

const DEFAULT_VITALS_CONFIG = [
  {
    id: "bloodPressure",
    name: "Blood Pressure",
    unit: "mmHg",
    enabled: true,
    isDefault: true,
    type: "systolic/diastolic",
  },
  {
    id: "heartRate",
    name: "Heart Rate",
    unit: "BPM",
    enabled: true,
    isDefault: true,
  },
  {
    id: "temperature",
    name: "Temperature",
    unit: "°C",
    enabled: true,
    isDefault: true,
  },
  {
    id: "respiratoryRate",
    name: "Respiratory Rate",
    unit: "BPM",
    enabled: true,
    isDefault: true,
  },
  {
    id: "o2Sat",
    name: "O2 Saturation",
    unit: "%",
    enabled: true,
    isDefault: true,
  },
  {
    id: "bloodSugar",
    name: "Blood Sugar",
    unit: "mg/dL",
    enabled: true,
    isDefault: true,
  },
];

const formatTimestampForDisplay = (timestamp) => {
  if (!timestamp) return "N/A";
  if (timestamp.toDate) {
    return timestamp.toDate().toLocaleString();
  }
  return "N/A";
};

const formatDateForInput = (timestamp) => {
  const date = timestamp ? timestamp.toDate() : new Date();

  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, "0");
  const day = date.getDate().toString().padStart(2, "0");
  const hours = date.getHours().toString().padStart(2, "0");
  const minutes = date.getMinutes().toString().padStart(2, "0");

  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

const VitalsPage = () => {
  const { currentUser, activeCareProfile, userRole } = useAuth();
  const [vitalsConfig, setVitalsConfig] = useState(DEFAULT_VITALS_CONFIG);
  const [vitalsData, setVitalsData] = useState({});
  const [notes, setNotes] = useState("");
  const [dateTime, setDateTime] = useState(formatDateForInput(null));
  const [isEditingVitals, setIsEditingVitals] = useState(false);
  const [currentVitalsEntryId, setCurrentVitalsEntryId] = useState(null);
  const [showVitalsForm, setShowVitalsForm] = useState(false);
  const [rawVitalsHistory, setRawVitalsHistory] = useState([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(true);
  const [visibleRecordsCount, setVisibleRecordsCount] = useState(5);
  const [sortOrder, setSortOrder] = useState("desc");
  const cardRefs = useRef({});
  const [reportStartDate, setReportStartDate] = useState("");
  const [reportEndDate, setReportEndDate] = useState("");
  const [generatedReportData, setGeneratedReportData] = useState([]);
  const [logoBase64, setLogoBase64] = useState(null);
  const [profileNameForReport, setProfileNameForReport] = useState("");
  const [isReportSectionVisible, setIsReportSectionVisible] = useState(false);
  const [isSubmittingVitals, setIsSubmittingVitals] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const isOwner = activeCareProfile?.userId === currentUser?.uid;
  const role = isOwner ? ROLES.OWNER : userRole?.[activeCareProfile?.id];

  const canWrite = hasPermission(role, FEATURES.VITALS, "write");
  const canDelete = hasPermission(role, FEATURES.VITALS, "delete");
  const canManage = hasPermission(role, FEATURES.VITALS, "manage");

  const activeVitals = vitalsConfig.filter((v) => v.enabled && !v.archived);

  // Fetch logo for PDF reports
  useEffect(() => {
    const fetchLogo = async () => {
      try {
        const response = await fetch("/logo.png");
        if (!response.ok) return;
        const blob = await response.blob();
        const reader = new FileReader();
        reader.onloadend = () => setLogoBase64(reader.result);
        reader.readAsDataURL(blob);
      } catch (error) {
        console.error("Error fetching logo:", error);
      }
    };
    fetchLogo();
  }, []);

  // Fetch vitals configuration when profile changes
  useEffect(() => {
    const fetchConfig = async () => {
      if (!activeCareProfile || !activeCareProfile.id) {
        setVitalsConfig(DEFAULT_VITALS_CONFIG);
        return;
      }
      setProfileNameForReport(activeCareProfile.profileName || "N/A");
      try {
        const profileRef = doc(db, "careProfiles", activeCareProfile.id);
        const docSnap = await getDoc(profileRef);
        if (docSnap.exists() && docSnap.data().vitalsConfig) {
          setVitalsConfig(docSnap.data().vitalsConfig);
        } else {
          setVitalsConfig(DEFAULT_VITALS_CONFIG);
        }
      } catch (error) {
        console.warn("Unable to load vitals configuration:", error);
        setVitalsConfig(DEFAULT_VITALS_CONFIG);
      }
    };
    fetchConfig();
  }, [activeCareProfile]);

  // Fetch vitals history when profile changes
  useEffect(() => {
    if (!currentUser || !activeCareProfile || !activeCareProfile.id) {
      setRawVitalsHistory([]);
      setIsLoadingHistory(false);
      return;
    }
    setIsLoadingHistory(true);
    const vitalsCollectionRef = collection(db, "vitalsLog");
    const q = query(
      vitalsCollectionRef,
      where("careProfileId", "==", activeCareProfile.id),
      orderBy("dateTime", "desc"),
    );
    const unsubscribe = onSnapshot(
      q,
      (querySnapshot) => {
        const history = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setRawVitalsHistory(history);
        setIsLoadingHistory(false);
      },
      (error) => {
        console.error("Error fetching vitals history: ", error);
        setIsLoadingHistory(false);
      },
    );
    return () => unsubscribe();
  }, [currentUser, activeCareProfile]);

  const sortedVitalsHistory = React.useMemo(() => {
    return [...rawVitalsHistory].sort((a, b) => {
      const dateA = a.dateTime?.toDate ? a.dateTime.toDate() : new Date(0);
      const dateB = b.dateTime?.toDate ? b.dateTime.toDate() : new Date(0);
      return sortOrder === "asc" ? dateA - dateB : dateB - dateA;
    });
  }, [rawVitalsHistory, sortOrder]);

  const handleLoadMore = () => setVisibleRecordsCount((prev) => prev + 5);

  const handleVitalsDataChange = (id, value, subField = null) => {
    if (subField) {
      setVitalsData((prev) => ({
        ...prev,
        [id]: { ...(prev[id] || {}), [subField]: value },
      }));
    } else {
      setVitalsData((prev) => ({ ...prev, [id]: value }));
    }
  };

  const resetVitalsForm = useCallback(() => {
    setVitalsData({});
    setNotes("");
    setDateTime(formatDateForInput(null));
    setIsEditingVitals(false);
    setCurrentVitalsEntryId(null);
  }, []);

  const handleCancelEdit = () => {
    const idToScrollTo = currentVitalsEntryId;
    resetVitalsForm();
    setShowVitalsForm(false);
    if (idToScrollTo && cardRefs.current[idToScrollTo]) {
      setTimeout(
        () =>
          cardRefs.current[idToScrollTo].scrollIntoView({
            behavior: "smooth",
            block: "center",
          }),
        100,
      );
    }
  };

  const handleSubmitVitals = async (e) => {
    e.preventDefault();
    if (!canWrite)
      return window.alert("You do not have permission to perform this action.");
    if (!currentUser || !activeCareProfile || !dateTime)
      return window.alert("Date & Time are required.");

    const processedVitals = {};
    activeVitals.forEach((vitalConf) => {
      if (vitalConf.type === "systolic/diastolic") {
        const systolic = parseFloat(vitalsData[vitalConf.id]?.systolic);
        const diastolic = parseFloat(vitalsData[vitalConf.id]?.diastolic);
        if (!isNaN(systolic) || !isNaN(diastolic)) {
          processedVitals[vitalConf.id] = {
            systolic: isNaN(systolic) ? null : systolic,
            diastolic: isNaN(diastolic) ? null : diastolic,
          };
        }
      } else {
        const value = parseFloat(vitalsData[vitalConf.id]);
        if (!isNaN(value)) {
          processedVitals[vitalConf.id] = value;
        }
      }
    });

    if (Object.keys(processedVitals).length === 0) {
      return window.alert("At least one vital sign must have a value.");
    }

    setIsSubmittingVitals(true);
    try {
      if (isEditingVitals) {
        // Update existing entry
        await commitFirestoreWrite(
          updateDoc(doc(db, "vitalsLog", currentVitalsEntryId), {
            vitals: processedVitals,
            dateTime: Timestamp.fromDate(new Date(dateTime)),
            notes: notes || "",
            lastModifiedAt: Timestamp.now(),
            lastModifiedBy: {
              uid: currentUser.uid,
              name: currentUser.displayName || currentUser.email,
            },
          }),
          "Update vitals entry",
        );
      } else {
        // Create new entry
        const vitalsRef = doc(collection(db, "vitalsLog"));
        await commitFirestoreWrite(
          setDoc(vitalsRef, {
            vitals: processedVitals,
            dateTime: Timestamp.fromDate(new Date(dateTime)),
            notes: notes || "",
            careProfileId: activeCareProfile.id,
            userId: activeCareProfile.userId,
            createdAt: Timestamp.now(),
            createdBy: {
              uid: currentUser.uid,
              name: currentUser.displayName || currentUser.email,
            },
            lastModifiedAt: Timestamp.now(),
            lastModifiedBy: {
              uid: currentUser.uid,
              name: currentUser.displayName || currentUser.email,
            },
          }),
          "Create vitals entry",
        );
      }

      window.alert(
        offlineAwareMessage(
          `Vitals ${isEditingVitals ? "updated" : "added"} successfully!`,
        ),
      );
      resetVitalsForm();
      setShowVitalsForm(false);
    } catch (error) {
      console.error("Error saving vitals: ", error);
      window.alert(`An error occurred: ${error.message}`);
    } finally {
      setIsSubmittingVitals(false);
    }
  };

  const handleSetEditVitals = (entry) => {
    if (!canWrite) return window.alert("You do not have permission to edit.");

    setShowVitalsForm(true);
    setIsEditingVitals(true);
    setCurrentVitalsEntryId(entry.id);
    setDateTime(formatDateForInput(entry.dateTime));
    setNotes(entry.notes || "");

    const flattenedVitals = {};
    activeVitals.forEach((vitalConfig) => {
      const vitalValue = entry.vitals?.[vitalConfig.id];
      if (
        vitalConfig.type === "systolic/diastolic" &&
        typeof vitalValue === "object"
      ) {
        flattenedVitals[vitalConfig.id] = {
          systolic: vitalValue.systolic ?? "",
          diastolic: vitalValue.diastolic ?? "",
        };
      } else {
        flattenedVitals[vitalConfig.id] = vitalValue ?? "";
      }
    });
    setVitalsData(flattenedVitals);

    setTimeout(
      () =>
        document
          .getElementById("vitals-form-section")
          ?.scrollIntoView({ behavior: "smooth" }),
      100,
    );
  };

  const handleDeleteVitals = async (id) => {
    if (!canDelete) {
      return window.alert("You do not have permission to delete this entry.");
    }
    if (!window.confirm("Are you sure you want to delete this entry?")) return;

    try {
      setDeletingId(id);
      await commitFirestoreWrite(
        deleteDoc(doc(db, "vitalsLog", id)),
        "Delete vitals entry",
      );
      window.alert(offlineAwareMessage("Vitals entry deleted successfully."));
    } catch (error) {
      console.error("Error deleting vitals: ", error);
      window.alert(`An error occurred: ${error.message}`);
    } finally {
      setDeletingId(null);
    }
  };

  const getVitalValue = (entry, vitalConfig) => {
    const value = entry.vitals?.[vitalConfig.id];
    if (value === null || value === undefined) return "N/A";
    if (vitalConfig.type === "systolic/diastolic") {
      const systolic = value.systolic ?? "-";
      const diastolic = value.diastolic ?? "-";
      return `${systolic}/${diastolic}`;
    }
    return value;
  };

  const handleGenerateReport = () => {
    // Build start/end using explicit local times to avoid timezone parsing quirks
    const start = reportStartDate
      ? new Date(`${reportStartDate}T00:00:00`)
      : new Date(0);
    const end = reportEndDate
      ? new Date(`${reportEndDate}T23:59:59.999`)
      : new Date();

    const filtered = rawVitalsHistory.filter((entry) => {
      const entryDate = entry.dateTime.toDate();
      return entryDate >= start && entryDate <= end;
    });
    setGeneratedReportData(filtered);
  };

  const handleExportToCSV = () => {
    if (generatedReportData.length === 0) {
      window.alert(
        "No data available to export. Please generate a report first.",
      );
      return;
    }

    // Ensure chronological (oldest-first) order for export only
    const dataAsc = [...generatedReportData].sort((a, b) => {
      const ta =
        typeof a.dateTime?.toDate === "function"
          ? a.dateTime.toDate().getTime()
          : 0;
      const tb =
        typeof b.dateTime?.toDate === "function"
          ? b.dateTime.toDate().getTime()
          : 0;
      return ta - tb;
    });

    const reportHeaders = [];
    activeVitals.forEach((vital) => {
      if (vital.type === "systolic/diastolic") {
        reportHeaders.push(`"${vital.name} (Systolic)"`);
        reportHeaders.push(`"${vital.name} (Diastolic)"`);
      } else {
        reportHeaders.push(`"${vital.name}"`);
      }
    });
    const headers = ["Date", "Time", ...reportHeaders, "Notes"];

    const csvContent = [
      headers.join(","),
      ...dataAsc.map((entry) => {
        const rowData = [];
        activeVitals.forEach((v) => {
          const value = entry.vitals?.[v.id];
          if (v.type === "systolic/diastolic") {
            rowData.push(`"${value?.systolic ?? "N/A"}"`);
            rowData.push(`"${value?.diastolic ?? "N/A"}"`);
          } else {
            rowData.push(
              `"${(value ?? "N/A").toString().replace(/"/g, '""')}"`,
            );
          }
        });
        return [
          `"${formatTimestampForDisplay(entry.dateTime).split(",")[0]}"`,
          `"${formatTimestampForDisplay(entry.dateTime).split(",")[1]?.trim()}"`,
          ...rowData,
          `"${(entry.notes || "").replace(/"/g, '""')}"`,
        ].join(",");
      }),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `${profileNameForReport}_vitals_report.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleExportToPDF = () => {
    if (generatedReportData.length === 0) {
      window.alert(
        "No data available to export. Please generate a report first.",
      );
      return;
    }
    // eslint-disable-next-line new-cap
    const doc = new jsPDF();

    const tableColumns = [];
    activeVitals.forEach((vital) => {
      if (vital.type === "systolic/diastolic") {
        tableColumns.push(`${vital.name} (Systolic)`);
        tableColumns.push(`${vital.name} (Diastolic)`);
      } else {
        tableColumns.push(vital.name);
      }
    });
    const head = [["Date", ...tableColumns, "Notes"]];

    // Ensure chronological (oldest-first) order for export only
    const dataAsc = [...generatedReportData].sort((a, b) => {
      const ta =
        typeof a.dateTime?.toDate === "function"
          ? a.dateTime.toDate().getTime()
          : 0;
      const tb =
        typeof b.dateTime?.toDate === "function"
          ? b.dateTime.toDate().getTime()
          : 0;
      return ta - tb;
    });

    const body = dataAsc.map((entry) => {
      const rowData = [];
      activeVitals.forEach((v) => {
        const value = entry.vitals?.[v.id];
        if (v.type === "systolic/diastolic") {
          rowData.push(value?.systolic ?? "N/A");
          rowData.push(value?.diastolic ?? "N/A");
        } else {
          rowData.push(value ?? "N/A");
        }
      });
      return [
        formatTimestampForDisplay(entry.dateTime),
        ...rowData,
        entry.notes || "",
      ];
    });

    autoTable(doc, {
      head: head,
      body: body,
      theme: "grid",
      margin: { top: 40 },
      headStyles: {
        fillColor: [220, 53, 69],
      },
      styles: {
        cellPadding: 2,
        fontSize: 8,
      },
      columnStyles: {
        0: { cellWidth: 35 },
      },
      didDrawPage: () => {
        if (logoBase64) {
          doc.addImage(logoBase64, "PNG", 14, 15, 20, 20);
        }
        doc.setFontSize(20);
        doc.text(`Vitals Report for ${profileNameForReport}`, 40, 22);
        doc.setFontSize(12);
        doc.text(
          `Report Period: ${reportStartDate} to ${reportEndDate}`,
          40,
          30,
        );
      },
    });

    doc.save(`${profileNameForReport}_vitals_report.pdf`);
  };

  const getSaveButtonLabel = () => {
    if (isSubmittingVitals) return "Saving...";
    return isEditingVitals ? "Update Vitals" : "Save Vitals";
  };

  if (!activeCareProfile) {
    return (
      <div className="p-8 text-center text-gray-500">
        Please select a care profile to view vitals.
      </div>
    );
  }

  return (
    <div className="container mx-auto p-4 sm:p-6 lg:p-8">
      <div className="flex flex-col sm:flex-row justify-between sm:items-center mb-6">
        <h1 className="text-3xl sm:text-4xl font-bold text-red-700 mb-4 sm:mb-0">
          Vitals Monitoring
        </h1>
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2">
          {canWrite && (
            <button
              onClick={() => {
                if (showVitalsForm) {
                  handleCancelEdit();
                } else {
                  resetVitalsForm();
                  setShowVitalsForm(true);
                  setTimeout(
                    () =>
                      document
                        .getElementById("vitals-form-section")
                        ?.scrollIntoView({ behavior: "smooth" }),
                    100,
                  );
                }
              }}
              className={`w-full sm:w-auto px-6 py-3 text-white font-semibold rounded-lg shadow-md transition duration-300 ease-in-out flex items-center justify-center gap-2 ${
                showVitalsForm
                  ? "bg-gray-500 hover:bg-gray-600"
                  : "bg-red-500 hover:bg-red-600"
              }`}
            >
              {showVitalsForm ? <FaWindowClose /> : <FaPlus />}
              {showVitalsForm ? "Cancel" : "Add New Vitals"}
            </button>
          )}
          {canManage && (
            <Link
              to={`/profiles/${activeCareProfile.id}/vitals-settings`}
              className="w-full sm:w-auto px-6 py-3 bg-gray-600 text-white font-semibold rounded-lg shadow-md hover:bg-gray-700 transition text-center"
            >
              Manage Vitals Fields
            </Link>
          )}
        </div>
      </div>

      {showVitalsForm && (
        <form
          id="vitals-form-section"
          onSubmit={handleSubmitVitals}
          className="bg-white p-6 rounded-lg shadow-lg border-l-4 border-red-500 mb-8"
        >
          <h3 className="text-2xl font-semibold text-gray-700 mb-6">
            {isEditingVitals ? "Edit Vitals Entry" : "Add New Vitals Entry"}
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
            <div className="md:col-span-2">
              <label
                htmlFor="dateTime"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Date & Time
              </label>
              <input
                type="datetime-local"
                id="dateTime"
                value={dateTime}
                onChange={(e) => setDateTime(e.target.value)}
                required
                className="w-full rounded-md border-gray-300 shadow-sm"
              />
            </div>
            {activeVitals.map((vital) => (
              <div key={vital.id}>
                <label
                  htmlFor={vital.id}
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  {vital.name} {vital.unit && `(${vital.unit})`}
                </label>
                {vital.type === "systolic/diastolic" ? (
                  <div className="flex gap-2">
                    <input
                      type="number"
                      id={`${vital.id}-systolic`}
                      placeholder="Systolic"
                      value={vitalsData[vital.id]?.systolic || ""}
                      onChange={(e) =>
                        handleVitalsDataChange(
                          vital.id,
                          e.target.value,
                          "systolic",
                        )
                      }
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
                    />
                    <input
                      type="number"
                      id={`${vital.id}-diastolic`}
                      placeholder="Diastolic"
                      value={vitalsData[vital.id]?.diastolic || ""}
                      onChange={(e) =>
                        handleVitalsDataChange(
                          vital.id,
                          e.target.value,
                          "diastolic",
                        )
                      }
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
                    />
                  </div>
                ) : (
                  <input
                    type="number"
                    step="0.01"
                    id={vital.id}
                    value={vitalsData[vital.id] || ""}
                    onChange={(e) =>
                      handleVitalsDataChange(vital.id, e.target.value)
                    }
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
                  />
                )}
              </div>
            ))}
            <div className="md:col-span-2">
              <label
                htmlFor="notes"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Notes
              </label>
              <textarea
                id="notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Add any relevant notes..."
                rows="4"
                className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500"
              ></textarea>
            </div>
          </div>

          <div className="flex items-center justify-end gap-4 mt-8">
            <button
              type="button"
              onClick={handleCancelEdit}
              className="px-6 py-2 bg-gray-200 text-gray-800 font-semibold rounded-lg hover:bg-gray-300"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmittingVitals}
              className={`px-6 py-2 font-semibold rounded-lg shadow-md flex items-center gap-2 ${isSubmittingVitals ? "bg-gray-400 cursor-not-allowed" : "bg-red-600 hover:bg-red-700 text-white"}`}
            >
              {getSaveButtonLabel()}
            </button>
          </div>
        </form>
      )}

      {/* Report Generation Section */}
      <div className="bg-white p-4 sm:p-6 rounded-lg shadow-md mb-8 border border-gray-200">
        <button
          onClick={() => setIsReportSectionVisible(!isReportSectionVisible)}
          className="w-full flex justify-between items-center text-left text-xl font-bold text-gray-800"
        >
          <span>Generate Vitals Report</span>
          {isReportSectionVisible ? <FaChevronUp /> : <FaChevronDown />}
        </button>

        {isReportSectionVisible && (
          <div className="mt-4 pt-4 border-t">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
              <div>
                <label
                  htmlFor="reportStartDate"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Start Date
                </label>
                <input
                  type="date"
                  id="reportStartDate"
                  value={reportStartDate}
                  onChange={(e) => setReportStartDate(e.target.value)}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
                />
              </div>
              <div>
                <label
                  htmlFor="reportEndDate"
                  className="block text-sm font-medium text-gray-700"
                >
                  End Date
                </label>
                <input
                  type="date"
                  id="reportEndDate"
                  value={reportEndDate}
                  onChange={(e) => setReportEndDate(e.target.value)}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
                />
              </div>
              <div className="flex justify-start">
                <button
                  onClick={handleGenerateReport}
                  className="w-full md:w-auto px-6 py-2 bg-blue-500 text-white font-semibold rounded-lg shadow-md hover:bg-blue-600"
                >
                  Generate Report
                </button>
              </div>
            </div>

            {generatedReportData.length > 0 && (
              <div className="mt-6">
                <h4 className="text-lg font-bold mb-2">
                  Report for {profileNameForReport} ({reportStartDate} to{" "}
                  {reportEndDate})
                </h4>
                <div className="flex gap-4 mb-4">
                  <button
                    onClick={handleExportToPDF}
                    className="px-4 py-2 bg-red-600 text-white font-semibold rounded-lg shadow-md hover:bg-red-700 flex items-center gap-2"
                  >
                    <FaFilePdf /> Export to PDF
                  </button>
                  <button
                    onClick={handleExportToCSV}
                    className="px-4 py-2 bg-green-600 text-white font-semibold rounded-lg shadow-md hover:bg-green-700 flex items-center gap-2"
                  >
                    <FaFileCsv /> Export to CSV
                  </button>
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Date
                        </th>
                        {activeVitals.map((v) => (
                          <th
                            key={v.id}
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase"
                          >
                            {v.name}
                          </th>
                        ))}
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Notes
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {generatedReportData.map((entry, index) => (
                        <tr key={index}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            {formatTimestampForDisplay(entry.dateTime)}
                          </td>
                          {activeVitals.map((v) => (
                            <td
                              key={v.id}
                              className="px-6 py-4 whitespace-nowrap text-sm"
                            >
                              {getVitalValue(entry, v)}
                            </td>
                          ))}
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            {entry.notes}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Vitals History Section */}
      {activeCareProfile && (
        <div className="mt-8 bg-white p-4 sm:p-6 rounded-lg shadow-lg">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-800">
              Vitals History
            </h2>
            <div className="flex items-center">
              <label htmlFor="sortOrder" className="mr-2 text-gray-600">
                Sort by:
              </label>
              <select
                id="sortOrder"
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value)}
                className="p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
              >
                <option value="desc">Newest First</option>
                <option value="asc">Oldest First</option>
              </select>
            </div>
          </div>

          {isLoadingHistory ? (
            <div className="text-center p-4">
              <p className="text-gray-500">Loading history...</p>
            </div>
          ) : sortedVitalsHistory.length === 0 ? (
            <div className="text-center p-4 border-t border-gray-200">
              <p className="text-gray-500">
                No vitals history recorded for this profile.
              </p>
            </div>
          ) : (
            <>
              {/* Card View for smaller screens (hidden on xl and up) */}
              <div className="xl:hidden">
                {sortedVitalsHistory
                  .slice(0, visibleRecordsCount)
                  .map((entry) => (
                    <div
                      key={entry.id}
                      ref={(el) => (cardRefs.current[entry.id] = el)}
                      className="bg-white rounded-lg shadow-md p-4 mb-4 border border-gray-200"
                    >
                      <div className="flex justify-between items-start">
                        <h3 className="font-bold text-lg text-gray-800 mb-2">
                          {formatTimestampForDisplay(entry.dateTime)}
                        </h3>
                        <div className="flex-shrink-0">
                          {canWrite && (
                            <button
                              onClick={() => handleSetEditVitals(entry)}
                              className="text-blue-600 hover:text-blue-900 mr-3"
                            >
                              <FaEdit size={18} />
                            </button>
                          )}
                          {canDelete && (
                            <button
                              onClick={() => handleDeleteVitals(entry.id)}
                              disabled={deletingId === entry.id}
                              className={`$${deletingId === entry.id ? "text-gray-400 cursor-not-allowed" : "text-red-600 hover:text-red-900"}`}
                            >
                              <FaTrash size={18} />
                            </button>
                          )}
                        </div>
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-sm">
                        {activeVitals.map((vitalConf) => {
                          const value = getVitalValue(entry, vitalConf);
                          if (value === "N/A") {
                            return null;
                          }
                          return (
                            <div key={vitalConf.id}>
                              <p className="font-semibold text-gray-600">
                                {vitalConf.name}
                              </p>
                              <p className="text-gray-800">{value}</p>
                            </div>
                          );
                        })}
                      </div>
                      {entry.notes && entry.notes !== "N/A" && (
                        <div className="mt-4 pt-2 border-t border-gray-200">
                          <p className="font-semibold text-gray-600">Notes</p>
                          <p className="text-gray-800 whitespace-normal break-words">
                            {entry.notes}
                          </p>
                        </div>
                      )}
                      {entry.lastModifiedBy &&
                        entry.userId !== entry.lastModifiedBy.uid && (
                          <span className="text-xs text-gray-400 block mt-2">
                            (Edited by {entry.lastModifiedBy.name})
                          </span>
                        )}
                    </div>
                  ))}
              </div>

              {/* Table View for larger screens (hidden below xl) */}
              <div className="hidden xl:block overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th
                        scope="col"
                        className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        Date & Time
                      </th>
                      {activeVitals.map((vital) => (
                        <th
                          key={vital.id}
                          scope="col"
                          className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                        >
                          {vital.name}
                        </th>
                      ))}
                      <th
                        scope="col"
                        className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        Notes
                      </th>
                      <th scope="col" className="relative px-4 py-3">
                        <span className="sr-only">Actions</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {sortedVitalsHistory
                      .slice(0, visibleRecordsCount)
                      .map((entry) => (
                        <tr
                          key={entry.id}
                          ref={(el) => (cardRefs.current[entry.id] = el)}
                          className="hover:bg-gray-50"
                        >
                          <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {formatTimestampForDisplay(entry.dateTime)}
                          </td>
                          {activeVitals.map((vitalConf) => {
                            const value = getVitalValue(entry, vitalConf);
                            return (
                              <td
                                key={vitalConf.id}
                                className="px-4 py-4 whitespace-nowrap text-sm text-gray-500"
                              >
                                {value === "N/A" ? "" : value}
                              </td>
                            );
                          })}
                          <td className="px-4 py-4 whitespace-normal text-sm text-gray-500 max-w-xs break-words">
                            {entry.notes && entry.notes !== "N/A"
                              ? entry.notes
                              : ""}
                            {entry.lastModifiedBy &&
                              entry.userId !== entry.lastModifiedBy.uid && (
                                <span className="text-xs text-gray-400 block mt-1">
                                  (Edited by {entry.lastModifiedBy.name})
                                </span>
                              )}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-right text-sm font-medium">
                            {canWrite && (
                              <button
                                onClick={() => handleSetEditVitals(entry)}
                                className="text-blue-600 hover:text-blue-900 mr-3"
                              >
                                <FaEdit />
                              </button>
                            )}
                            {canDelete && (
                              <button
                                onClick={() => handleDeleteVitals(entry.id)}
                                disabled={deletingId === entry.id}
                                className={`$${deletingId === entry.id ? "text-gray-400 cursor-not-allowed" : "text-red-600 hover:text-red-900"}`}
                              >
                                <FaTrash />
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>

              {visibleRecordsCount < sortedVitalsHistory.length && (
                <div className="mt-6 text-center">
                  <button
                    onClick={handleLoadMore}
                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors"
                  >
                    Load More
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}

      <MultiActionScrollButton scrollContainerId="main-content" />
    </div>
  );
};

export default VitalsPage;
